import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, FormControlLabel, Switch, TextField } from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationTriangle } from "@fortawesome/pro-solid-svg-icons";

import type { IEquipmentTypeSlim, IManufacturer, IResponseWithLoading } from "App/Modules/Models";
import { EquipmentClass } from "App/Modules/Models";
import type { IEquipmentTypeRepository, IManufacturersRepository } from "App/Modules/Repositories";
import { extendWithLoadingAndFail } from "App/Modules/Services";

import { useDebounce } from "../../../Shared";
import { useManufacturers } from "./useManufacturers";

type AddEquipmentTypeProps = {
    mmEquipmentTypeRepository: IEquipmentTypeRepository;
    irManufacturersRepository: IManufacturersRepository;
    onClose: () => void;
    onAddSuccess?: (equipmentType: IEquipmentTypeSlim) => void;
};

const useDebouncedValidator = (model: any, validator: () => void, delay = 300) => {
    const debouncedModel = useDebounce(model, delay);
    useEffect(() => {
        if (debouncedModel) {
            validator();
        }
    }, [model, debouncedModel, validator]);
};

export const AddEquipmentType = (props: AddEquipmentTypeProps) => {
    const { t } = useTranslation();
    const types = Object.keys(EquipmentClass)
        .map((k) => EquipmentClass[k])
        .filter((v) => typeof v === "string")
        .sort();
    const [model, setModel] = useState("");
    const [version, setVersion] = useState("");
    const [type, setType] = useState("");
    const [enableIpInterface, setEnableIpInterface] = useState(false);
    const [manufacturer, setManufacturer] = useState("");
    const { manufacturers } = useManufacturers(props.irManufacturersRepository);
    const [saveError, setSaveError] = useState(false);

    const [selectedManufacturer, setSelectedManufacturer] = useState<IManufacturer>(null);
    const [selectedType, setSelectedType] = useState<EquipmentClass>(null);

    const [modelValid, setModelValid] = useState(true);
    const [manufacturerValid, setManufacturerValid] = useState(true);
    const [typeValid, setTypeValid] = useState(true);

    const [saving, setSaving] = useState(false);

    const validateModel = () => {
        const valid = model.length > 0;
        setModelValid(valid);
        return valid;
    };

    const validateManufacturer = () => {
        const valid = selectedManufacturer !== null;
        setManufacturerValid(valid);
        return valid;
    };

    const validateType = () => {
        const valid = selectedType !== null;
        setTypeValid(valid);
        return valid;
    };

    useDebouncedValidator(model, validateModel);
    useDebouncedValidator(manufacturer, validateManufacturer);
    useDebouncedValidator(type, validateType);

    // I prefer it this way :)
    // eslint-disable-next-line prettier/prettier
    // prettier-ignore
    const validateForm = () => [
        validateModel(), 
        validateManufacturer(), 
        validateType()
    ].every((isValid) => isValid);

    const handleAddEquipmentType = () => {
        // hack, because tests does not translate translations
        const typeKey = type.startsWith("Enums.EquipmentClass.") ? type.slice(21) : type;
        const equipmentType = {
            manufacturer: selectedManufacturer,
            supportIpInterface: enableIpInterface,
            model,
            version,
            class: EquipmentClass[typeKey as keyof typeof EquipmentClass],
        } as IEquipmentTypeSlim;

        setSaving(true);

        props.mmEquipmentTypeRepository
            .addEquipmentType(equipmentType)
            .pipe(extendWithLoadingAndFail)
            .subscribe(
                (response: IResponseWithLoading<IEquipmentTypeSlim>) => {
                    if (!response.isLoading && response.data) {
                        if (props.onAddSuccess) {
                            props.onAddSuccess(response.data);
                        }
                        setSaving(false);
                        props.onClose();
                    }
                },
                () => {
                    setSaving(false);
                    setSaveError(true);
                },
            );
    };

    return (
        <div className="card dialog">
            <form className="dialog__content form">
                <div className="dialog__section dialog__section--full">
                    <h1 className="title title--h1 title--no-spacing">
                        {t("Gui.Buttons.AddEntity", {
                            entityName: t("Entities.EquipmentType.EquipmentType"),
                        })}
                    </h1>
                </div>

                <div className="dialog__section dialog__section--full">
                    {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                    <label htmlFor="search-manufacturer">Manufacturer</label>

                    <Autocomplete
                        options={manufacturers?.data || ([] as IManufacturer[])}
                        getOptionLabel={(p) => p.name}
                        value={selectedManufacturer}
                        inputValue={manufacturer}
                        loading={manufacturers?.isLoading}
                        onChange={(e, value: IManufacturer) => setSelectedManufacturer(value)}
                        onInputChange={(e, value) => setManufacturer(value)}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                id="search-manufacturer"
                                variant="outlined"
                                size="small"
                                data-testid="select-manufacturer"
                                error={!manufacturerValid}
                                helperText={!manufacturerValid ? t("Validation.Mixed.Required") : null}
                                onBlur={() => {
                                    validateManufacturer();
                                }}
                            />
                        )}
                    />
                </div>

                <div className="dialog__section dialog__section--full">
                    <label htmlFor="model">{t("Entities.EquipmentType.Model")}</label>
                    <TextField
                        id="model"
                        required
                        fullWidth
                        variant="outlined"
                        size="small"
                        type="text"
                        autoComplete="off"
                        helperText={!modelValid ? t("Validation.Mixed.Required") : null}
                        error={!modelValid}
                        onBlur={() => {
                            validateModel();
                        }}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            setModel(e.target.value);
                        }}
                    />
                </div>

                <div className="dialog__section dialog__section--full">
                    <label htmlFor="version">{t("Entities.EquipmentType.Version")}</label>
                    <TextField
                        id="version"
                        fullWidth
                        variant="outlined"
                        size="small"
                        type="text"
                        autoComplete="off"
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            setVersion(e.target.value);
                        }}
                    />
                </div>

                <div className="dialog__section dialog__section--full">
                    <label className="caption" htmlFor="type">
                        {t("Entities.EquipmentType.Type")}
                    </label>

                    <Autocomplete
                        options={types}
                        getOptionLabel={(p) => t(`Enums.EquipmentClass.${p as string}`)}
                        value={selectedType}
                        inputValue={type}
                        onChange={(e, value: EquipmentClass) => setSelectedType(value)}
                        onInputChange={(e, value) => setType(value)}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                variant="outlined"
                                size="small"
                                data-testid="select-type"
                                error={!typeValid}
                                helperText={!typeValid ? t("Validation.Mixed.Required") : null}
                                onBlur={() => {
                                    validateType();
                                }}
                            />
                        )}
                    />
                </div>

                <div className="dialog__section dialog__section--full">
                    <FormControlLabel
                        control={
                            <Switch
                                checked={enableIpInterface}
                                onChange={() => setEnableIpInterface((current) => !current)}
                            />
                        }
                        label="Support IP interface"
                    />
                </div>

                {manufacturers && manufacturers?.hasError ? (
                    <div className="dialog__section dialog__section--full dialog__section--noscroll">
                        <div className="notice notice--critical">
                            <FontAwesomeIcon className="notice__icon" fixedWidth icon={faExclamationTriangle} />
                            <p className="caption">{t("Error.ErrorLoading")}</p>
                        </div>
                    </div>
                ) : null}

                {saveError ? (
                    <div className="dialog__section dialog__section--full dialog__section--noscroll">
                        <div className="notice notice--critical">
                            <FontAwesomeIcon className="notice__icon" fixedWidth icon={faExclamationTriangle} />
                            <p className="caption">{t("Error.ErrorSaving")}</p>
                        </div>
                    </div>
                ) : null}

                <div className="dialog__section dialog__section--buttons">
                    <Button
                        size="small"
                        color="default"
                        onClick={() => {
                            props.onClose();
                        }}
                    >
                        {t("Gui.Buttons.Cancel")}
                    </Button>
                    <Button
                        type="submit"
                        size="small"
                        color="primary"
                        variant="contained"
                        disabled={saving}
                        onClick={(e) => {
                            e.preventDefault();
                            if (validateForm()) {
                                handleAddEquipmentType();
                            }
                        }}
                    >
                        {t("Gui.Buttons.Add")}
                    </Button>
                </div>
            </form>
        </div>
    );
};
